// =============================================================================
// Simple Store - V1.5 Writing Tool
// Direct stories table operations (no content_items/versions complexity)
// =============================================================================

import { supabase } from './supabaseClient';
import {
  LanguageCode as TypesLanguageCode,
  Platform,
  ItemType,
  TvFormat,
  PlatformMetadata,
  DEFAULT_PLATFORM,
  DEFAULT_ITEM_TYPE,
  DEFAULT_TV_FORMAT,
  TV_FORMAT_DURATION_TARGETS,
  estimateDuration,
  PlatformLanguageConfig,
  TvPacingConfig,
  TvWordTargets,
  DEFAULT_PACING,
  StoryStatus as TypesStoryStatus,
  StoryFact,
  StoryRevision,
  EditorFeedbackCard,
} from '../types';

// =============================================================================
// TYPES
// =============================================================================

// Custom error for optimistic concurrency conflicts
export class VersionConflictError extends Error {
  constructor() {
    super('This story was updated in another tab or device. Please copy any unsaved text and refresh to see the latest changes.');
    this.name = 'VersionConflictError';
  }
}

export type LanguageCode = TypesLanguageCode;
export type StoryStatus = TypesStoryStatus;

export interface SimpleStory {
  id: string;
  // Web article fields
  headline: string;
  deck: string;
  body: string;
  language: LanguageCode;
  status: StoryStatus;
  // Platform & format (TV-first workflow)
  platform: Platform;
  itemType: ItemType;
  // Platform-specific metadata (TV fields stored as JSONB)
  platformMetadata: PlatformMetadata;
  // Translation linking
  groupId?: string;
  isTranslation: boolean;
  sourceLanguage?: LanguageCode;
  // Canonical tracking (for canonical vs derivative review)
  isCanonical: boolean;
  derivedFromStoryId?: string;
  lockedAt?: string; // Timestamp when story was approved/locked
  // Original content (for diff tracking)
  originalHeadline?: string;
  originalDeck?: string;
  originalBody?: string;
  // AI workflow data
  editorIssues?: any[];
  socialPosts?: any;
  // ENPS parity: assignments, locking, scratchpad
  assigneeId?: string;
  checkedOutBy?: string;
  checkedOutAt?: string;
  scratchpad?: string;
  editorialNotes?: string;
  version: number;
  // Editor approval workflow
  rejectionNotes?: string;
  submittedForReviewAt?: string;
  reviewedBy?: string;
  editorFeedback?: EditorFeedbackCard[];
  // Metadata
  authorId: string;
  createdAt: string;
  updatedAt: string;
}

export interface StoryGroup {
  groupId: string;
  stories: SimpleStory[];
  primaryStory: SimpleStory; // The original or first story
  availableLanguages: LanguageCode[];
  missingLanguages: LanguageCode[];
}

// =============================================================================
// CREATE - Start a new story
// =============================================================================

export async function createSimpleStory(data: {
  // Web article fields
  headline?: string;
  deck?: string;
  body?: string;
  // TV script fields
  slug?: string;
  anchorIntro?: string;
  script?: string;
  // Common fields
  language: LanguageCode;
  platform?: Platform;
  itemType?: ItemType;
  tvFormat?: TvFormat;
}): Promise<SimpleStory | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    console.error('createSimpleStory: No authenticated user');
    return null;
  }

  try {
    const groupId = crypto.randomUUID();
    const platform = data.platform || DEFAULT_PLATFORM;
    const tvFormat = data.tvFormat || (platform === 'tv' ? DEFAULT_TV_FORMAT : undefined);

    // Calculate duration for TV stories
    const durationTarget = tvFormat ? TV_FORMAT_DURATION_TARGETS[tvFormat] : undefined;
    const durationEstimate = platform === 'tv'
      ? estimateDuration(data.anchorIntro || '', data.script || '')
      : undefined;

    const { data: story, error } = await supabase
      .from('stories')
      .insert({
        // Web article fields
        headline: data.headline || '',
        deck: data.deck || '',
        body: data.body || '',
        // Platform-specific metadata (JSONB)
        platform_metadata: buildPlatformMetadataForDb({
          slug: data.slug,
          anchorIntro: data.anchorIntro,
          script: data.script,
          tvFormat,
          durationTarget,
          durationEstimate,
        }),
        // Platform fields
        language: data.language,
        platform: platform,
        item_type: data.itemType || DEFAULT_ITEM_TYPE,
        status: 'draft',
        group_id: groupId,
        is_translation: false,
        original_headline: data.headline || '',
        original_deck: data.deck || '',
        original_body: data.body || '',
        author_id: user.id,
      })
      .select()
      .single();

    if (error || !story) {
      console.error('Error creating story:', error);
      return null;
    }

    // Log action (fail silently if audit_logs has issues)
    try {
      const { error: auditError } = await supabase.from('audit_logs').insert({
        user_id: user.id,
        user_name: user.email?.split('@')[0] || 'Unknown',
        action: 'create_story',
        entity_type: 'story',
        entity_id: story.id,
      });
      if (auditError) {
        console.warn('[AuditLog] Failed to log create_story:', auditError.message);
      }
    } catch (auditErr) {
      console.warn('[AuditLog] Exception logging create_story:', auditErr);
    }

    return mapDbToStory(story);
  } catch (error) {
    console.error('createSimpleStory failed:', error);
    return null;
  }
}

// =============================================================================
// READ - Get stories for current user (or all users if admin)
// =============================================================================

// Helper to check if current user is admin
async function isCurrentUserAdmin(): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .single();

    return profile?.role === 'admin' || profile?.role === 'superuser';
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

// Helper to check if current user is editor or above
async function isCurrentUserEditorOrAbove(): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .single();

    return ['editor', 'supervisor', 'admin', 'superuser'].includes(profile?.role || '');
  } catch (error) {
    console.error('Error checking editor status:', error);
    return false;
  }
}

export async function getSimpleStories(options?: {
  limit?: number;
  language?: LanguageCode;
  status?: StoryStatus;
  allUsers?: boolean; // If true and user is admin, get all users' stories
  authorId?: string; // Filter by specific author
  dateFrom?: string; // Filter stories from this date (ISO string)
  dateTo?: string; // Filter stories to this date (ISO string)
  dateField?: 'created' | 'updated'; // Filter by created_at or updated_at (default: 'created')
}): Promise<SimpleStory[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  try {
    // Check if we should get all users' stories
    const getAllUsers = options?.allUsers && await isCurrentUserAdmin();

    let query = supabase
      .from('stories')
      .select('*')
      .neq('status', 'archived')
      .order('updated_at', { ascending: false });

    // Only filter by author_id if not getting all users' stories and no specific authorId filter
    if (!getAllUsers && !options?.authorId) {
      query = query.eq('author_id', user.id);
    }

    // Filter by specific author if provided
    if (options?.authorId) {
      query = query.eq('author_id', options.authorId);
    }

    // Filter by date range (created_at or updated_at)
    const dateField = options?.dateField || 'created';

    if (options?.dateFrom) {
      if (dateField === 'updated') {
        query = query.gte('updated_at', options.dateFrom);
      } else {
        query = query.gte('created_at', options.dateFrom);
      }
    }
    if (options?.dateTo) {
      // Add one day to include the entire end date
      const endDate = new Date(options.dateTo);
      endDate.setHours(23, 59, 59, 999);
      if (dateField === 'updated') {
        query = query.lte('updated_at', endDate.toISOString());
      } else {
        query = query.lte('created_at', endDate.toISOString());
      }
    }

    if (options?.language) {
      query = query.eq('language', options.language);
    }

    if (options?.status) {
      query = query.eq('status', options.status);
    }

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching stories:', error);
      return [];
    }

    return (data || []).map(mapDbToStory);
  } catch (error) {
    console.error('getSimpleStories failed:', error);
    return [];
  }
}

// =============================================================================
// READ - Get single story
// =============================================================================

export async function getSimpleStory(storyId: string): Promise<SimpleStory | null> {
  try {
    const { data, error } = await supabase
      .from('stories')
      .select('*')
      .eq('id', storyId)
      .single();

    if (error || !data) {
      console.error('Error fetching story:', error);
      return null;
    }

    return mapDbToStory(data);
  } catch (error) {
    console.error('getSimpleStory failed:', error);
    return null;
  }
}

// =============================================================================
// UPDATE - Save story content
// =============================================================================

export async function saveSimpleStory(
  storyId: string,
  data: {
    // Web article fields
    headline?: string;
    deck?: string;
    body?: string;
    // TV script fields
    slug?: string;
    anchorIntro?: string;
    script?: string;
    // Original content for diff tracking
    originalHeadline?: string;
    originalDeck?: string;
    originalBody?: string;
    // Common fields
    language?: LanguageCode;
    status?: StoryStatus;
    editorIssues?: any[];
    socialPosts?: any;
    // ENPS parity
    scratchpad?: string;
    editorialNotes?: string;
    // Editor approval workflow
    rejectionNotes?: string;
    editorFeedback?: EditorFeedbackCard[];
  },
  /** Current version from the in-memory story — required for OCC */
  expectedVersion?: number
): Promise<SimpleStory | null> {
  const [{ data: { user } }, userIsAdmin, userIsEditor] = await Promise.all([
    supabase.auth.getUser(),
    isCurrentUserAdmin(),
    isCurrentUserEditorOrAbove(),
  ]);
  if (!user) return null;

  try {
    const updateData: any = {};

    // Web article fields
    if (data.headline !== undefined) updateData.headline = data.headline;
    if (data.deck !== undefined) updateData.deck = data.deck;
    if (data.body !== undefined) updateData.body = data.body;

    // TV script fields → merge into platform_metadata JSONB
    const hasTvFields = data.slug !== undefined || data.anchorIntro !== undefined || data.script !== undefined;
    if (hasTvFields) {
      // SELECT current platform_metadata to merge new fields into
      const { data: currentRow } = await supabase
        .from('stories')
        .select('platform_metadata')
        .eq('id', storyId)
        .single();

      const currentMeta: Record<string, unknown> = (currentRow?.platform_metadata as Record<string, unknown>) || {};
      const newMeta = { ...currentMeta };
      if (data.slug !== undefined) newMeta.slug = data.slug;
      if (data.anchorIntro !== undefined) newMeta.anchor_intro = data.anchorIntro;
      if (data.script !== undefined) newMeta.script = data.script;

      // Auto-calculate duration estimate if TV fields are being updated
      if (data.anchorIntro !== undefined || data.script !== undefined) {
        const ai = data.anchorIntro !== undefined ? data.anchorIntro : (currentMeta.anchor_intro as string || '');
        const sc = data.script !== undefined ? data.script : (currentMeta.script as string || '');
        newMeta.duration_estimate = estimateDuration(ai, sc);
      }

      updateData.platform_metadata = newMeta;
    }

    // Original content for diff tracking
    if (data.originalHeadline !== undefined) updateData.original_headline = data.originalHeadline;
    if (data.originalDeck !== undefined) updateData.original_deck = data.originalDeck;
    if (data.originalBody !== undefined) updateData.original_body = data.originalBody;

    // Common fields
    if (data.language !== undefined) updateData.language = data.language;
    if (data.status !== undefined) updateData.status = data.status;
    if (data.editorIssues !== undefined) updateData.editor_issues = data.editorIssues;
    if (data.socialPosts !== undefined) updateData.social_posts = data.socialPosts;

    // ENPS parity
    if (data.scratchpad !== undefined) updateData.scratchpad = data.scratchpad;
    if (data.editorialNotes !== undefined) updateData.editorial_notes = data.editorialNotes;

    // Editor approval workflow
    if (data.rejectionNotes !== undefined) updateData.rejection_notes = data.rejectionNotes;
    if (data.editorFeedback !== undefined) updateData.editor_feedback = data.editorFeedback;

    // OCC: bump version on every save
    if (expectedVersion !== undefined) {
      updateData.version = expectedVersion + 1;
    }

    let query = supabase
      .from('stories')
      .update(updateData)
      .eq('id', storyId);

    // OCC: only update if version matches what the caller last read
    if (expectedVersion !== undefined) {
      query = query.eq('version', expectedVersion);
    }

    // Only filter by author_id if user is not admin/editor
    // RLS policies handle authorization for editor+ roles
    if (!userIsAdmin && !userIsEditor) {
      query = query.eq('author_id', user.id);
    }

    const { data: rows, error } = await query
      .select();

    if (error) {
      console.error('Error saving story:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        storyId,
        updateData: JSON.stringify(updateData),
        isAdmin: userIsAdmin
      });
      return null;
    }

    // OCC: if version was checked and 0 rows came back, someone else saved first
    if (expectedVersion !== undefined && (!rows || rows.length === 0)) {
      throw new VersionConflictError();
    }

    if (!rows || rows.length === 0) {
      return null;
    }

    return mapDbToStory(rows[0]);
  } catch (error) {
    // Re-throw VersionConflictError so callers can catch it specifically
    if (error instanceof VersionConflictError) throw error;
    console.error('saveSimpleStory failed:', error);
    return null;
  }
}

// =============================================================================
// STATUS UPDATES (Phase 3 - draft/reviewed/approved workflow)
// =============================================================================

/**
 * Set story status to 'reviewed' (AI review complete, ready for approval)
 */
export async function setReviewedStatus(storyId: string): Promise<boolean> {
  const result = await saveSimpleStory(storyId, { status: 'reviewed' });
  return result !== null;
}

/**
 * Set story status back to 'draft' (for editing)
 */
export async function setDraftStatus(storyId: string): Promise<boolean> {
  const result = await saveSimpleStory(storyId, { status: 'draft' });
  return result !== null;
}

/**
 * Unlock an approved story (sets status back to draft).
 * The database trigger will auto-clear locked_at.
 */
export async function unlockStory(storyId: string): Promise<boolean> {
  // Clear editor issues on unlock — they were for the approved version and are stale after editing resumes
  const result = await saveSimpleStory(storyId, { status: 'draft', editorIssues: [] });
  return result !== null;
}

/**
 * Check if a story is locked (approved status)
 */
export function isStoryLocked(story: SimpleStory): boolean {
  return story.status === 'approved';
}

// =============================================================================
// EDITOR APPROVAL WORKFLOW
// =============================================================================

/**
 * Submit story for editor review (reviewed → pending_review).
 */
export async function submitForReview(storyId: string): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const result = await saveSimpleStory(storyId, { status: 'pending_review' as StoryStatus });
  if (!result) return false;

  // Audit log (non-blocking)
  try {
    await supabase.from('audit_logs').insert({
      user_id: user.id,
      user_name: user.email?.split('@')[0] || 'Unknown',
      action: 'submit_for_review',
      entity_type: 'story',
      entity_id: storyId,
    });
  } catch (e) { console.warn('[AuditLog] submit_for_review:', e); }

  return true;
}

/**
 * Editor approves a story (pending_review → approved).
 * DB trigger validates editor role and auto-sets reviewed_by.
 */
export async function editorApproveStory(storyId: string): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const { error } = await supabase
    .from('stories')
    .update({
      status: 'approved',
      editor_issues: [],
      editor_feedback: [],
      locked_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', storyId);

  if (error) {
    console.error('Error approving story:', error);
    return false;
  }

  // Audit log (non-blocking)
  try {
    await supabase.from('audit_logs').insert({
      user_id: user.id,
      user_name: user.email?.split('@')[0] || 'Unknown',
      action: 'approve_story',
      entity_type: 'story',
      entity_id: storyId,
      details: 'Editor approved story',
    });
  } catch (e) { console.warn('[AuditLog] approve_story:', e); }

  return true;
}

/**
 * Editor sends story back to journalist (pending_review → draft).
 * DB trigger validates editor role and auto-sets reviewed_by.
 */
export async function editorRejectStory(storyId: string, rejectionNotes: string, editorFeedback?: EditorFeedbackCard[]): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const updateData: Record<string, unknown> = {
    status: 'draft',
    rejection_notes: rejectionNotes,
    updated_at: new Date().toISOString(),
  };
  if (editorFeedback !== undefined) {
    updateData.editor_feedback = editorFeedback;
  }

  const { error } = await supabase
    .from('stories')
    .update(updateData)
    .eq('id', storyId);

  if (error) {
    console.error('Error rejecting story:', error);
    return false;
  }

  // Audit log (non-blocking)
  try {
    await supabase.from('audit_logs').insert({
      user_id: user.id,
      user_name: user.email?.split('@')[0] || 'Unknown',
      action: 'reject_story',
      entity_type: 'story',
      entity_id: storyId,
      details: JSON.stringify({ rejection_notes: rejectionNotes }),
    });
  } catch (e) { console.warn('[AuditLog] reject_story:', e); }

  return true;
}

/**
 * Get all stories pending editor review (FIFO order).
 */
export async function getPendingReviewStories(): Promise<SimpleStory[]> {
  try {
    const { data, error } = await supabase
      .from('stories')
      .select('*')
      .eq('status', 'pending_review')
      .order('submitted_for_review_at', { ascending: true });

    if (error) {
      console.error('Error fetching pending review stories:', error);
      return [];
    }

    return (data || []).map(mapDbToStory);
  } catch (error) {
    console.error('getPendingReviewStories failed:', error);
    return [];
  }
}

// =============================================================================
// DELETE - Archive a story (soft delete)
// =============================================================================

export async function archiveSimpleStory(storyId: string): Promise<boolean> {
  const [{ data: { user } }, userIsAdmin, userIsEditor] = await Promise.all([
    supabase.auth.getUser(),
    isCurrentUserAdmin(),
    isCurrentUserEditorOrAbove(),
  ]);
  if (!user) return false;

  try {
    let query = supabase
      .from('stories')
      .update({ status: 'archived' })
      .eq('id', storyId);

    // Only filter by author_id if user is not admin/editor
    // RLS policies handle authorization for editor+ roles
    if (!userIsAdmin && !userIsEditor) {
      query = query.eq('author_id', user.id);
    }

    const { error } = await query;

    if (error) {
      console.error('Error archiving story:', error);
      return false;
    }

    // Log action (fail silently if audit_logs has issues)
    try {
      const { error: auditError } = await supabase.from('audit_logs').insert({
        user_id: user.id,
        user_name: user.email?.split('@')[0] || 'Unknown',
        action: 'archive_story',
        entity_type: 'story',
        entity_id: storyId,
      });
      if (auditError) {
        console.warn('[AuditLog] Failed to log archive_story:', auditError.message);
      }
    } catch (auditErr) {
      console.warn('[AuditLog] Exception logging archive_story:', auditErr);
    }

    return true;
  } catch (error) {
    console.error('archiveSimpleStory failed:', error);
    return false;
  }
}

// =============================================================================
// TRANSLATION - Create a translation of an existing story
// =============================================================================

export async function createTranslation(
  sourceStoryId: string,
  targetLanguage: LanguageCode,
  translatedContent: {
    headline: string;
    deck: string;
    body: string;
    // TV script fields (optional)
    slug?: string;
    anchorIntro?: string;
    script?: string;
  }
): Promise<SimpleStory | null> {
  const [{ data: { user } }, sourceStory] = await Promise.all([
    supabase.auth.getUser(),
    getSimpleStory(sourceStoryId),
  ]);
  if (!user) return null;

  try {
    if (!sourceStory) {
      console.error('Source story not found');
      return null;
    }

    // Determine if this is a TV story - inherit from source
    const isTvPlatform = sourceStory.platform === 'tv';
    const tvFormat = isTvPlatform ? sourceStory.platformMetadata.tvFormat : undefined;
    const durationTarget = isTvPlatform ? sourceStory.platformMetadata.durationTarget : undefined;
    const durationEstimate = isTvPlatform
      ? estimateDuration(translatedContent.anchorIntro || '', translatedContent.script || '')
      : undefined;

    const { data: story, error } = await supabase
      .from('stories')
      .insert({
        // Web article fields
        headline: translatedContent.headline,
        deck: translatedContent.deck,
        body: translatedContent.body,
        // Platform-specific metadata (JSONB)
        platform_metadata: buildPlatformMetadataForDb({
          slug: translatedContent.slug,
          anchorIntro: translatedContent.anchorIntro,
          script: translatedContent.script,
          tvFormat,
          durationTarget,
          durationEstimate,
        }),
        // Platform fields - inherit from source
        platform: sourceStory.platform,
        item_type: sourceStory.itemType,
        // Language and status
        language: targetLanguage,
        status: 'draft',
        // Group and translation metadata
        group_id: sourceStory.groupId,
        is_translation: true,
        is_canonical: false,
        derived_from_story_id: sourceStoryId,
        source_language: sourceStory.language,
        // Original content for diff tracking
        original_headline: translatedContent.headline,
        original_deck: translatedContent.deck,
        original_body: translatedContent.body,
        author_id: user.id,
      })
      .select()
      .single();

    if (error || !story) {
      console.error('Error creating translation:', error);
      return null;
    }

    // Log action (fail silently if audit_logs has issues)
    try {
      const { error: auditError } = await supabase.from('audit_logs').insert({
        user_id: user.id,
        user_name: user.email?.split('@')[0] || 'Unknown',
        action: 'create_translation',
        entity_type: 'story',
        entity_id: story.id,
        details: JSON.stringify({ source_id: sourceStoryId, target_language: targetLanguage }),
      });
      if (auditError) {
        console.warn('[AuditLog] Failed to log create_translation:', auditError.message);
      }
    } catch (auditErr) {
      console.warn('[AuditLog] Exception logging create_translation:', auditErr);
    }

    return mapDbToStory(story);
  } catch (error) {
    console.error('createTranslation failed:', error);
    return null;
  }
}

// =============================================================================
// GET TRANSLATIONS - Get all translations for a story group
// =============================================================================

export async function getTranslations(groupId: string): Promise<SimpleStory[]> {
  try {
    const { data, error } = await supabase
      .from('stories')
      .select('*')
      .eq('group_id', groupId)
      .neq('status', 'archived')
      .order('language');

    if (error) {
      console.error('Error fetching translations:', error);
      return [];
    }

    return (data || []).map(mapDbToStory);
  } catch (error) {
    console.error('getTranslations failed:', error);
    return [];
  }
}

// =============================================================================
// STORY GROUPS - Get grouped stories
// =============================================================================

const SUPPORTED_LANGUAGES: LanguageCode[] = ['eng', 'fa-AF', 'ps-AF'];

// Helper to get primary story (non-translation, or first by creation date)
function getPrimaryStory(stories: SimpleStory[]): SimpleStory {
  // First try to find a non-translation story
  const nonTranslation = stories.find(s => !s.isTranslation);
  if (nonTranslation) return nonTranslation;
  
  // Otherwise, return the oldest story
  return stories.sort((a, b) => 
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )[0];
}

// Get canonical story (non-translation, or oldest if all are translations)
// This ensures translations always use the original source, not a translation
export function getCanonicalStory(group: StoryGroup): SimpleStory {
  // First try to find a non-translation story
  const nonTranslation = group.stories.find(s => !s.isTranslation);
  if (nonTranslation) return nonTranslation;
  
  // If all are translations, return the oldest (original source)
  return group.stories.sort((a, b) => 
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )[0];
}

// Helper to ensure story has group_id (for legacy data)
async function ensureGroupId(story: SimpleStory): Promise<string> {
  if (story.groupId) return story.groupId;
  
  // Assign a new group_id to orphaned story
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return crypto.randomUUID();
  
  const newGroupId = crypto.randomUUID();
  const { error } = await supabase
    .from('stories')
    .update({ group_id: newGroupId })
    .eq('id', story.id)
    .eq('author_id', user.id);
  
  if (error) {
    console.warn('Failed to assign group_id to story:', error);
    return crypto.randomUUID();
  }
  
  return newGroupId;
}

// Get a single story group by group_id
export async function getStoryGroup(groupId: string): Promise<StoryGroup | null> {
  try {
    const stories = await getTranslations(groupId);
    if (stories.length === 0) return null;
    
    const primaryStory = getPrimaryStory(stories);
    const availableLanguages = stories.map(s => s.language);
    const missingLanguages = SUPPORTED_LANGUAGES.filter(
      lang => !availableLanguages.includes(lang)
    );
    
    return {
      groupId,
      stories,
      primaryStory,
      availableLanguages,
      missingLanguages,
    };
  } catch (error) {
    console.error('getStoryGroup failed:', error);
    return null;
  }
}

// Get all story groups for current user (or all users if admin)
export async function getStoryGroups(options?: {
  limit?: number;
  language?: LanguageCode;
  status?: StoryStatus;
  allUsers?: boolean; // If true and user is admin, get all users' stories
  authorId?: string; // Filter by specific author
  dateFrom?: string; // Filter stories from this date (ISO string)
  dateTo?: string; // Filter stories to this date (ISO string)
  dateField?: 'created' | 'updated'; // Filter by created_at or updated_at (default: 'created')
}): Promise<StoryGroup[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  try {
    // First, get all stories (pass through all filter options)
    const allStories = await getSimpleStories({ ...options, allUsers: options?.allUsers });
    
    // Group stories by group_id
    const groupMap = new Map<string, SimpleStory[]>();
    
    for (const story of allStories) {
      // Ensure story has group_id
      let groupId = story.groupId;
      if (!groupId) {
        groupId = await ensureGroupId(story);
        if (!groupId) continue;
      }
      
      if (!groupMap.has(groupId)) {
        groupMap.set(groupId, []);
      }
      groupMap.get(groupId)!.push({ ...story, groupId });
    }
    
    // Convert map to StoryGroup array
    const groups: StoryGroup[] = [];
    for (const [groupId, stories] of groupMap.entries()) {
      if (stories.length === 0) continue;
      
      const primaryStory = getPrimaryStory(stories);
      const availableLanguages = stories.map(s => s.language);
      const missingLanguages = SUPPORTED_LANGUAGES.filter(
        lang => !availableLanguages.includes(lang)
      );
      
      groups.push({
        groupId,
        stories,
        primaryStory,
        availableLanguages,
        missingLanguages,
      });
    }
    
    // Sort by most recently updated (using primary story's updated_at)
    groups.sort((a, b) => 
      new Date(b.primaryStory.updatedAt).getTime() - 
      new Date(a.primaryStory.updatedAt).getTime()
    );
    
    // Apply limit if specified
    if (options?.limit) {
      return groups.slice(0, options.limit);
    }
    
    return groups;
  } catch (error) {
    console.error('getStoryGroups failed:', error);
    return [];
  }
}

// =============================================================================
// STORY GRID - Phase 3 derivative generation UI
// =============================================================================

import { StoryGridData, PlatformLanguageConfig as GridPlatformLanguageConfig } from '../types';

/**
 * Get all stories in a group organized by platform and language for grid display.
 * Returns a matrix structure: { [platform]: { [language]: SimpleStory | null } }
 */
export async function getStoryGridData(groupId: string): Promise<StoryGridData | null> {
  try {
    const [stories, platformConfig] = await Promise.all([
      getTranslations(groupId),
      getPlatformLanguageConfig(),
    ]);
    if (stories.length === 0) return null;

    const canonicalStory = stories.find(s => s.isCanonical) || stories[0];

    // Initialize grid with null values for all valid platform-language combos
    const platforms: Platform[] = ['tv', 'web', 'social'];
    const languages: LanguageCode[] = ['fa-AF', 'ps-AF', 'eng'];

    const grid: Record<Platform, Record<LanguageCode, SimpleStory | null>> = {
      tv: { 'fa-AF': null, 'ps-AF': null, 'eng': null },
      web: { 'fa-AF': null, 'ps-AF': null, 'eng': null },
      social: { 'fa-AF': null, 'ps-AF': null, 'eng': null },
      radio: { 'fa-AF': null, 'ps-AF': null, 'eng': null },
      all: { 'fa-AF': null, 'ps-AF': null, 'eng': null },
    };

    // Populate grid with existing stories
    for (const story of stories) {
      if (grid[story.platform] && grid[story.platform][story.language] === null) {
        grid[story.platform][story.language] = story;
      }
    }

    // Determine title from canonical
    const title = canonicalStory.platformMetadata.slug || canonicalStory.headline || 'Untitled';

    return {
      groupId,
      canonicalStory,
      grid,
      platformLanguageConfig: platformConfig,
      title,
    };
  } catch (error) {
    console.error('getStoryGridData failed:', error);
    return null;
  }
}

/**
 * Get all story groups with grid data for the story list view.
 * More efficient than calling getStoryGridData for each group.
 */
export async function getStoryGridList(options?: {
  language?: LanguageCode;
  authorId?: string;
  limit?: number;
}): Promise<StoryGridData[]> {
  try {
    const [groups, platformConfig] = await Promise.all([
      getStoryGroups(options),
      getPlatformLanguageConfig(),
    ]);

    const platforms: Platform[] = ['tv', 'web', 'social'];
    const languages: LanguageCode[] = ['fa-AF', 'ps-AF', 'eng'];

    return groups.map(group => {
      const canonicalStory = group.stories.find(s => s.isCanonical) || group.primaryStory;

      // Initialize grid
      const grid: Record<Platform, Record<LanguageCode, SimpleStory | null>> = {
        tv: { 'fa-AF': null, 'ps-AF': null, 'eng': null },
        web: { 'fa-AF': null, 'ps-AF': null, 'eng': null },
        social: { 'fa-AF': null, 'ps-AF': null, 'eng': null },
        radio: { 'fa-AF': null, 'ps-AF': null, 'eng': null },
        all: { 'fa-AF': null, 'ps-AF': null, 'eng': null },
      };

      // Populate grid
      for (const story of group.stories) {
        if (grid[story.platform] && grid[story.platform][story.language] === null) {
          grid[story.platform][story.language] = story;
        }
      }

      return {
        groupId: group.groupId,
        canonicalStory,
        grid,
        platformLanguageConfig: platformConfig,
        title: canonicalStory.platformMetadata.slug || canonicalStory.headline || 'Untitled',
      };
    });
  } catch (error) {
    console.error('getStoryGridList failed:', error);
    return [];
  }
}

// =============================================================================
// GET USERS WITH STORIES - Get only users who have created stories
// =============================================================================

export interface UserWithStories {
  id: string;
  full_name: string | null;
  email: string | null;
}

export async function getUsersWithStories(): Promise<UserWithStories[]> {
  try {
    // Get distinct author_ids from stories table
    const { data: stories, error: storiesError } = await supabase
      .from('stories')
      .select('author_id')
      .not('author_id', 'is', null)
      .neq('status', 'archived');

    if (storiesError) {
      console.error('Error fetching story authors:', storiesError);
      return [];
    }

    // Get unique author IDs
    const authorIds = [...new Set((stories || []).map(s => s.author_id).filter(Boolean))];

    if (authorIds.length === 0) {
      return [];
    }

    // Fetch user profiles for these authors
    const { data: users, error: usersError } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .in('id', authorIds)
      .order('full_name', { ascending: true });

    if (usersError) {
      console.error('Error fetching user profiles:', usersError);
      return [];
    }

    return (users || []).map(user => ({
      id: user.id,
      full_name: user.full_name,
      email: user.email,
    }));
  } catch (error) {
    console.error('getUsersWithStories failed:', error);
    return [];
  }
}

// =============================================================================
// HELPER - Build platform_metadata JSONB for DB writes (camelCase → snake_case)
// =============================================================================

function buildPlatformMetadataForDb(fields: {
  slug?: string;
  anchorIntro?: string;
  script?: string;
  tvFormat?: TvFormat;
  durationTarget?: number;
  durationEstimate?: number;
}): Record<string, unknown> {
  const meta: Record<string, unknown> = {};
  if (fields.slug !== undefined) meta.slug = fields.slug;
  if (fields.anchorIntro !== undefined) meta.anchor_intro = fields.anchorIntro;
  if (fields.script !== undefined) meta.script = fields.script;
  if (fields.tvFormat !== undefined) meta.tv_format = fields.tvFormat;
  if (fields.durationTarget !== undefined) meta.duration_target = fields.durationTarget;
  if (fields.durationEstimate !== undefined) meta.duration_estimate = fields.durationEstimate;
  return meta;
}

// =============================================================================
// HELPER - Map database row to SimpleStory
// =============================================================================

function mapDbToStory(row: any): SimpleStory {
  const pm = row.platform_metadata || {};
  return {
    id: row.id,
    // Web article fields
    headline: row.headline || '',
    deck: row.deck || '',
    body: row.body || '',
    language: row.language || 'fa-AF',
    status: row.status || 'draft',
    // Platform & format
    platform: row.platform || 'tv',
    itemType: row.item_type || 'video',
    // Platform-specific metadata (snake_case → camelCase)
    platformMetadata: {
      slug: pm.slug,
      anchorIntro: pm.anchor_intro,
      script: pm.script,
      tvFormat: pm.tv_format,
      durationTarget: pm.duration_target,
      durationEstimate: pm.duration_estimate,
    },
    // Translation linking
    groupId: row.group_id,
    isTranslation: row.is_translation || false,
    sourceLanguage: row.source_language,
    // Canonical tracking
    isCanonical: row.is_canonical ?? true,  // Default to true for backwards compatibility
    derivedFromStoryId: row.derived_from_story_id,
    // Original content
    originalHeadline: row.original_headline,
    originalDeck: row.original_deck,
    originalBody: row.original_body,
    // AI workflow
    editorIssues: row.editor_issues,
    socialPosts: row.social_posts,
    // ENPS parity: assignments, locking, scratchpad
    assigneeId: row.assignee_id,
    checkedOutBy: row.checked_out_by,
    checkedOutAt: row.checked_out_at,
    scratchpad: row.scratchpad,
    editorialNotes: row.editorial_notes,
    version: row.version ?? 1,
    // Editor approval workflow
    rejectionNotes: row.rejection_notes || undefined,
    submittedForReviewAt: row.submitted_for_review_at || undefined,
    reviewedBy: row.reviewed_by || undefined,
    editorFeedback: row.editor_feedback || [],
    // Metadata
    authorId: row.author_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// =============================================================================
// PLATFORM-LANGUAGE CONFIGURATION
// =============================================================================

// Cache for platform-language config (refreshed on each call, short TTL)
let platformLanguageConfigCache: PlatformLanguageConfig[] | null = null;
let platformLanguageConfigCacheTime: number = 0;
const PLATFORM_LANG_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch platform-language configuration from the database.
 * This determines which language/platform combinations are valid.
 * Results are cached for 5 minutes.
 */
export async function getPlatformLanguageConfig(): Promise<PlatformLanguageConfig[]> {
  // Return cached config if still valid
  if (platformLanguageConfigCache && Date.now() - platformLanguageConfigCacheTime < PLATFORM_LANG_CACHE_TTL) {
    return platformLanguageConfigCache;
  }

  try {
    const { data, error } = await supabase.rpc('get_platform_language_config');

    if (error) {
      console.error('Error fetching platform-language config:', error);
      // Return cached data if available, otherwise empty array
      return platformLanguageConfigCache || [];
    }

    // Map database rows to PlatformLanguageConfig
    const config: PlatformLanguageConfig[] = (data || []).map((row: any) => ({
      platform: row.platform as Platform,
      language: row.language as LanguageCode,
      allowed: row.allowed,
      isCanonicalSource: row.is_canonical_source,
    }));

    // Update cache
    platformLanguageConfigCache = config;
    platformLanguageConfigCacheTime = Date.now();

    return config;
  } catch (error) {
    console.error('getPlatformLanguageConfig failed:', error);
    return platformLanguageConfigCache || [];
  }
}

/**
 * Check if a platform-language combination is allowed.
 */
export async function isPlatformLanguageAllowed(platform: Platform, language: LanguageCode): Promise<boolean> {
  const config = await getPlatformLanguageConfig();
  const entry = config.find(c => c.platform === platform && c.language === language);
  return entry?.allowed ?? true; // Default to allowed if not configured
}

/**
 * Check if a platform-language combination can be a canonical source.
 */
export async function isCanonicalSourceAllowed(platform: Platform, language: LanguageCode): Promise<boolean> {
  const config = await getPlatformLanguageConfig();
  const entry = config.find(c => c.platform === platform && c.language === language && c.allowed);
  return entry?.isCanonicalSource ?? false;
}

/**
 * Get allowed languages for a given platform.
 */
export async function getAllowedLanguagesForPlatform(platform: Platform): Promise<LanguageCode[]> {
  const config = await getPlatformLanguageConfig();
  return config
    .filter(c => c.platform === platform && c.allowed)
    .map(c => c.language);
}

/**
 * Get allowed platforms for a given language.
 */
export async function getAllowedPlatformsForLanguage(language: LanguageCode): Promise<Platform[]> {
  const config = await getPlatformLanguageConfig();
  return config
    .filter(c => c.language === language && c.allowed)
    .map(c => c.platform);
}

// =============================================================================
// DERIVATIVE STORIES
// =============================================================================

/**
 * Create a derivative story from a canonical source.
 * Derivative stories get light review (style only).
 */
export async function createDerivativeStory(
  canonicalStoryId: string,
  targetPlatform: Platform,
  targetLanguage: LanguageCode,
  content: {
    // Web article fields
    headline?: string;
    deck?: string;
    body?: string;
    // TV script fields
    slug?: string;
    anchorIntro?: string;
    script?: string;
  }
): Promise<SimpleStory | null> {
  const [{ data: { user } }, canonicalStory] = await Promise.all([
    supabase.auth.getUser(),
    getSimpleStory(canonicalStoryId),
  ]);
  if (!user) {
    console.error('createDerivativeStory: No authenticated user');
    return null;
  }

  try {
    if (!canonicalStory) {
      console.error('createDerivativeStory: Canonical story not found');
      return null;
    }

    const isTvPlatform = targetPlatform === 'tv';
    const tvFormat = isTvPlatform ? (canonicalStory.platformMetadata.tvFormat || DEFAULT_TV_FORMAT) : undefined;
    const durationTarget = tvFormat ? TV_FORMAT_DURATION_TARGETS[tvFormat] : undefined;
    const durationEstimate = isTvPlatform
      ? estimateDuration(content.anchorIntro || '', content.script || '')
      : undefined;

    const { data: story, error } = await supabase
      .from('stories')
      .insert({
        // Web article fields
        headline: content.headline || '',
        deck: content.deck || '',
        body: content.body || '',
        // Platform-specific metadata (JSONB)
        platform_metadata: buildPlatformMetadataForDb({
          slug: content.slug,
          anchorIntro: content.anchorIntro,
          script: content.script,
          tvFormat,
          durationTarget,
          durationEstimate,
        }),
        // Platform fields
        language: targetLanguage,
        platform: targetPlatform,
        item_type: isTvPlatform ? 'video' : 'text',
        status: 'draft',
        // Inherit group from canonical
        group_id: canonicalStory.groupId,
        // Mark as derivative
        is_canonical: false,
        derived_from_story_id: canonicalStoryId,
        is_translation: targetLanguage !== canonicalStory.language,
        source_language: canonicalStory.language,
        // Original content for diff tracking
        original_headline: content.headline || '',
        original_deck: content.deck || '',
        original_body: content.body || '',
        // Metadata
        author_id: user.id,
      })
      .select()
      .single();

    if (error || !story) {
      console.error('Error creating derivative story:', error);
      return null;
    }

    // Log action
    try {
      await supabase.from('audit_logs').insert({
        user_id: user.id,
        user_name: user.email?.split('@')[0] || 'Unknown',
        action: 'create_derivative',
        entity_type: 'story',
        entity_id: story.id,
        details: JSON.stringify({
          canonical_id: canonicalStoryId,
          target_platform: targetPlatform,
          target_language: targetLanguage,
        }),
      });
    } catch (auditErr) {
      console.warn('[AuditLog] Exception logging create_derivative:', auditErr);
    }

    return mapDbToStory(story);
  } catch (error) {
    console.error('createDerivativeStory failed:', error);
    return null;
  }
}

/**
 * Get all derivatives of a canonical story.
 */
export async function getStoryDerivatives(canonicalStoryId: string): Promise<SimpleStory[]> {
  try {
    const { data, error } = await supabase
      .from('stories')
      .select('*')
      .eq('derived_from_story_id', canonicalStoryId)
      .neq('status', 'archived')
      .order('platform')
      .order('language');

    if (error) {
      console.error('Error fetching story derivatives:', error);
      return [];
    }

    return (data || []).map(mapDbToStory);
  } catch (error) {
    console.error('getStoryDerivatives failed:', error);
    return [];
  }
}

/**
 * Find an existing derivative story by group+platform+language.
 * Returns null if no derivative exists for this combination.
 */
export async function getExistingDerivative(
  groupId: string,
  platform: Platform,
  language: LanguageCode,
): Promise<SimpleStory | null> {
  try {
    const { data, error } = await supabase
      .from('stories')
      .select('*')
      .eq('group_id', groupId)
      .eq('platform', platform)
      .eq('language', language)
      .neq('status', 'archived')
      .maybeSingle();

    if (error || !data) return null;
    return mapDbToStory(data);
  } catch {
    return null;
  }
}

/**
 * Get all valid derivative platform-language combinations for a canonical story.
 * Excludes the canonical's own combo and any disabled combos.
 */
export async function getDerivativeCombinations(
  canonicalStory: SimpleStory
): Promise<Array<{ platform: Platform; language: LanguageCode }>> {
  const config = await getPlatformLanguageConfig();
  return config
    .filter(c => {
      if (!c.allowed) return false;
      // Skip the canonical's own platform-language
      if (c.platform === canonicalStory.platform && c.language === canonicalStory.language) return false;
      // Only tv, web, social
      if (!['tv', 'web', 'social'].includes(c.platform)) return false;
      return true;
    })
    .map(c => ({ platform: c.platform as Platform, language: c.language as LanguageCode }));
}

/**
 * Create empty derivative draft rows for all valid combinations.
 * Skips any that already exist in the group.
 */
export async function createAllDerivativeDrafts(
  canonicalStory: SimpleStory
): Promise<{ created: SimpleStory[]; existing: SimpleStory[]; skipped: number; errors: number }> {
  console.log('[createAllDerivativeDrafts] canonical:', {
    id: canonicalStory.id,
    platform: canonicalStory.platform,
    language: canonicalStory.language,
    groupId: canonicalStory.groupId,
  });

  const combos = await getDerivativeCombinations(canonicalStory);
  console.log('[createAllDerivativeDrafts] combos:', combos);

  if (combos.length === 0) {
    console.warn('[createAllDerivativeDrafts] No derivative combinations found!');
    return { created: [], existing: [], skipped: 0, errors: 0 };
  }

  type SkippedResult = { kind: 'skipped'; story: SimpleStory };
  const results = await Promise.allSettled(
    combos.map(async (combo): Promise<SimpleStory | SkippedResult> => {
      // Check if derivative already exists
      if (canonicalStory.groupId) {
        const existingDeriv = await getExistingDerivative(
          canonicalStory.groupId,
          combo.platform,
          combo.language
        );
        if (existingDeriv) {
          console.log(`[createAllDerivativeDrafts] Skipping ${combo.platform}/${combo.language} — already exists (${existingDeriv.id})`);
          return { kind: 'skipped', story: existingDeriv };
        }
      }

      console.log(`[createAllDerivativeDrafts] Creating ${combo.platform}/${combo.language}...`);
      const result = await createDerivativeStory(
        canonicalStory.id,
        combo.platform,
        combo.language,
        { headline: '', deck: '', body: '' }
      );
      if (result) {
        console.log(`[createAllDerivativeDrafts] Created ${combo.platform}/${combo.language}: ${result.id}`);
        return result;
      } else {
        // createDerivativeStory returned null — treat as error, not skip
        throw new Error(`createDerivativeStory returned null for ${combo.platform}/${combo.language}`);
      }
    })
  );

  const created: SimpleStory[] = [];
  const existing: SimpleStory[] = [];
  let skipped = 0;
  let errors = 0;

  for (let i = 0; i < results.length; i++) {
    const result = results[i];
    if (result.status === 'fulfilled') {
      const val = result.value;
      if (val && typeof val === 'object' && 'kind' in val && val.kind === 'skipped') {
        skipped++;
        existing.push(val.story);
      } else {
        created.push(val as SimpleStory);
      }
    } else {
      errors++;
      console.error(`[createAllDerivativeDrafts] combo[${i}] (${combos[i].platform}/${combos[i].language}) rejected:`, result.reason);
    }
  }

  console.log('[createAllDerivativeDrafts] final:', { created: created.length, existing: existing.length, skipped, errors });
  return { created, existing, skipped, errors };
}

// =============================================================================
// TV PACING CONFIGURATION
// =============================================================================

// Cache for pacing config (5 minute TTL)
let pacingConfigCache: Record<LanguageCode, TvPacingConfig> | null = null;
let pacingConfigCacheTime = 0;
const PACING_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Cache for word targets (5 minute TTL)
let wordTargetsCache: Record<string, TvWordTargets> | null = null;
let wordTargetsCacheTime = 0;

/**
 * Get TV pacing configuration for a language.
 * Fetches from database with caching.
 */
export async function getTvPacingConfig(language: LanguageCode): Promise<TvPacingConfig> {
  const now = Date.now();

  // Check cache
  if (pacingConfigCache && (now - pacingConfigCacheTime) < PACING_CACHE_TTL) {
    const cached = pacingConfigCache[language];
    if (cached) {
      return cached;
    }
  }

  try {
    // Fetch all pacing configs at once
    const { data, error } = await supabase
      .from('tv_pacing_config')
      .select('language, content_type, words_per_second');

    if (error || !data) {
      console.warn('[TvPacing] Error fetching config, using defaults:', error);
      return DEFAULT_PACING[language] || DEFAULT_PACING['eng'];
    }

    // Build cache
    const newCache: Record<LanguageCode, TvPacingConfig> = {
      'fa-AF': { anchorWps: 2.13, scriptWps: 2.46 },
      'ps-AF': { anchorWps: 2.00, scriptWps: 2.33 },
      'eng': { anchorWps: 2.50, scriptWps: 2.50 },
    };

    for (const row of data) {
      const lang = row.language as LanguageCode;
      if (!newCache[lang]) {
        newCache[lang] = { anchorWps: 2.5, scriptWps: 2.5 };
      }
      if (row.content_type === 'anchor') {
        newCache[lang].anchorWps = Number(row.words_per_second);
      } else if (row.content_type === 'script') {
        newCache[lang].scriptWps = Number(row.words_per_second);
      }
    }

    pacingConfigCache = newCache;
    pacingConfigCacheTime = now;

    console.log('[TvPacing] Loaded pacing config:', Object.keys(newCache).length, 'languages');

    return newCache[language] || DEFAULT_PACING['eng'];
  } catch (error) {
    console.error('[TvPacing] Exception fetching config:', error);
    return DEFAULT_PACING[language] || DEFAULT_PACING['eng'];
  }
}

/**
 * Get TV word count targets for a format and language.
 */
export async function getTvWordTargets(
  tvFormat: TvFormat,
  language: LanguageCode
): Promise<TvWordTargets> {
  const now = Date.now();
  const cacheKey = `${tvFormat}-${language}`;

  // Check cache
  if (wordTargetsCache && (now - wordTargetsCacheTime) < PACING_CACHE_TTL) {
    const cached = wordTargetsCache[cacheKey];
    if (cached) {
      return cached;
    }
  }

  try {
    // Fetch all word targets at once
    const { data, error } = await supabase
      .from('tv_word_targets')
      .select('tv_format, language, anchor_words, script_words, duration_target');

    if (error || !data) {
      console.warn('[TvPacing] Error fetching word targets, using defaults:', error);
      return getDefaultWordTargets(tvFormat, language);
    }

    // Build cache
    const newCache: Record<string, TvWordTargets> = {};

    for (const row of data) {
      const key = `${row.tv_format}-${row.language}`;
      newCache[key] = {
        anchorWords: row.anchor_words,
        scriptWords: row.script_words,
        durationTarget: row.duration_target,
      };
    }

    wordTargetsCache = newCache;
    wordTargetsCacheTime = now;

    console.log('[TvPacing] Loaded word targets:', Object.keys(newCache).length, 'combinations');

    return newCache[cacheKey] || getDefaultWordTargets(tvFormat, language);
  } catch (error) {
    console.error('[TvPacing] Exception fetching word targets:', error);
    return getDefaultWordTargets(tvFormat, language);
  }
}

/**
 * Get default word targets when database is unavailable
 */
function getDefaultWordTargets(tvFormat: TvFormat, language: LanguageCode): TvWordTargets {
  const pacing = DEFAULT_PACING[language] || DEFAULT_PACING['eng'];
  const durationTarget = TV_FORMAT_DURATION_TARGETS[tvFormat];

  // Assume 15s anchor, rest for script
  const anchorDuration = 15;
  const scriptDuration = durationTarget - anchorDuration;

  return {
    anchorWords: Math.round(anchorDuration * pacing.anchorWps),
    scriptWords: Math.round(scriptDuration * pacing.scriptWps),
    durationTarget,
  };
}

/**
 * Clear pacing config cache (useful for testing or after config updates)
 */
export function clearPacingConfigCache(): void {
  pacingConfigCache = null;
  pacingConfigCacheTime = 0;
  wordTargetsCache = null;
  wordTargetsCacheTime = 0;
}

// =============================================================================
// STORY FACTS (Facts Stream)
// =============================================================================

/**
 * Get all facts for a story group.
 * Uses the get_facts_for_group RPC function from migration 062.
 * Returns only current (non-superseded) facts by default.
 */
export async function getFactsForGroup(groupId: string): Promise<StoryFact[]> {
  if (!groupId) {
    console.warn('[Facts] getFactsForGroup called with empty groupId');
    return [];
  }

  try {
    const { data, error } = await supabase.rpc('get_facts_for_group', {
      p_group_id: groupId
    });

    if (error) {
      console.error('[Facts] Error fetching facts for group:', error);
      return [];
    }

    if (!data || !Array.isArray(data)) {
      return [];
    }

    // Map database results and filter to only current facts
    return data
      .filter((row: Record<string, unknown>) => row.is_current === true)
      .map((row: Record<string, unknown>): StoryFact => ({
        id: row.id as string,
        content: row.content as string,
        fact_type: row.fact_type as StoryFact['fact_type'],
        language: row.language as string,
        source_attribution: row.source_attribution as string | undefined,
        source_type: row.source_type as StoryFact['source_type'] | undefined,
        completeness: (row.completeness as StoryFact['completeness']) || 'complete',
        completeness_note: row.completeness_note as string | undefined,
        confidence: (row.confidence as StoryFact['confidence']) || 'attributed',
        confidence_note: row.confidence_note as string | undefined,
        extracted_at: row.extracted_at as string,
        extracted_from_story: row.extracted_from_story as string | undefined,
        superseded_by: row.superseded_by as string | undefined,
        is_current: row.is_current as boolean | undefined,
      }));
  } catch (error) {
    console.error('[Facts] Exception fetching facts for group:', error);
    return [];
  }
}

// =============================================================================
// PESSIMISTIC LOCKING (ENPS Parity - Checkout)
// =============================================================================

/** Stale lock threshold in milliseconds (3 minutes) */
const STALE_LOCK_MS = 3 * 60 * 1000;

/**
 * Attempt to check out (lock) a story for editing.
 * Returns { success, lockedByName } — if locked by someone else and not stale,
 * success=false and lockedByName tells who holds the lock.
 */
export async function checkoutStory(storyId: string): Promise<{
  success: boolean;
  lockedByName?: string;
  lockedByUserId?: string;
}> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { success: false };

  try {
    // Fetch current lock state
    const { data: story, error } = await supabase
      .from('stories')
      .select('checked_out_by, checked_out_at')
      .eq('id', storyId)
      .single();

    if (error || !story) return { success: false };

    const now = Date.now();
    const lockedBy = story.checked_out_by;
    const lockedAt = story.checked_out_at ? new Date(story.checked_out_at).getTime() : 0;
    const isStale = lockedAt > 0 && (now - lockedAt) > STALE_LOCK_MS;

    // If locked by someone else and not stale, deny
    if (lockedBy && lockedBy !== user.id && !isStale) {
      // Fetch name of lock holder
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', lockedBy)
        .single();

      return {
        success: false,
        lockedByName: profile?.full_name || profile?.email || 'Another user',
        lockedByUserId: lockedBy,
      };
    }

    // Claim the lock
    const { error: updateError } = await supabase
      .from('stories')
      .update({
        checked_out_by: user.id,
        checked_out_at: new Date().toISOString(),
      })
      .eq('id', storyId);

    if (updateError) {
      console.error('[Checkout] Failed to claim lock:', updateError);
      return { success: false };
    }

    return { success: true };
  } catch (err) {
    console.error('[Checkout] Exception:', err);
    return { success: false };
  }
}

/**
 * Release a checkout lock (set checked_out_by/at to null).
 * Only the lock holder (or admin) can release.
 */
export async function releaseCheckout(storyId: string): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  try {
    const { error } = await supabase
      .from('stories')
      .update({
        checked_out_by: null,
        checked_out_at: null,
      })
      .eq('id', storyId);

    if (error) {
      console.error('[Checkout] Failed to release lock:', error);
      return false;
    }
    return true;
  } catch (err) {
    console.error('[Checkout] Release exception:', err);
    return false;
  }
}

/**
 * Heartbeat: update checked_out_at to now() to keep the lock alive.
 * Called every 60 seconds while a story is open for editing.
 */
export async function heartbeatCheckout(storyId: string): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  try {
    const { error } = await supabase
      .from('stories')
      .update({ checked_out_at: new Date().toISOString() })
      .eq('id', storyId)
      .eq('checked_out_by', user.id);

    if (error) {
      console.error('[Checkout] Heartbeat failed:', error);
      return false;
    }
    return true;
  } catch (err) {
    console.error('[Checkout] Heartbeat exception:', err);
    return false;
  }
}

// =============================================================================
// REVISION HISTORY (ENPS Parity)
// =============================================================================

/**
 * Get revision history for a story, ordered by most recent first.
 */
export async function getStoryRevisions(
  storyId: string,
  limit = 20,
  offset = 0,
): Promise<StoryRevision[]> {
  try {
    const { data, error } = await supabase
      .from('story_revisions')
      .select('*')
      .eq('story_id', storyId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      console.error('[Revisions] Error fetching:', error);
      return [];
    }

    return (data || []).map((row: any): StoryRevision => ({
      id: row.id,
      storyId: row.story_id,
      snapshot: row.snapshot,
      changedBy: row.changed_by,
      createdAt: row.created_at,
    }));
  } catch (err) {
    console.error('[Revisions] Exception:', err);
    return [];
  }
}

/**
 * Batch fetch user profiles by IDs.
 * Returns a Map<uuid, { fullName, email }> for efficient lookups.
 */
export async function getProfilesByIds(
  userIds: string[],
): Promise<Map<string, { fullName: string; email: string }>> {
  const map = new Map<string, { fullName: string; email: string }>();
  if (userIds.length === 0) return map;

  const unique = [...new Set(userIds)];

  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .in('id', unique);

    if (error) {
      console.error('[Profiles] Batch fetch error:', error);
      return map;
    }

    for (const row of data || []) {
      map.set(row.id, {
        fullName: row.full_name || row.email || 'Unknown user',
        email: row.email || '',
      });
    }
  } catch (err) {
    console.error('[Profiles] Batch fetch exception:', err);
  }

  return map;
}
